import { useState } from "react";
import { Mail, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.subscribeNewsletter({ email: trimmed, name: name.trim() || undefined });
      if (res.already_subscribed) {
        toast.success("You're already on our list — thank you!");
      } else {
        toast.success("Welcome to the family.", {
          description: "We'll share stories of impact and ways to help.",
        });
      }
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="bg-[#F4F1EB] border-t border-[#E2DCD0]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-24">
        <div className="rounded-[2rem] bg-[#FAF8F5] border border-[#E2DCD0] overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 p-10 sm:p-14">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 mb-5">
                <Mail size={14} className="text-[#692128]" />
                <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8B2635]">
                  Stay in Touch
                </span>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#2C2A29] leading-[1.05] tracking-tight">
                Stories of grace,
                <br />
                <span className="italic text-[#692128]">straight to your inbox.</span>
              </h2>
              <p className="mt-5 text-[#5C5856] text-lg max-w-md">
                Family stories, program updates, and meaningful ways to help — no clutter, no spam.
              </p>
            </div>

            <div className="lg:col-span-6 flex items-center">
              {done ? (
                <div
                  data-testid="newsletter-success"
                  className="w-full rounded-2xl bg-[#2A4747] text-[#F4F1EB] p-8 text-center"
                >
                  <div className="text-xs tracking-[0.22em] uppercase font-bold text-[#C89F5D] mb-2">
                    Thank you
                  </div>
                  <p className="font-serif text-2xl">You're on the list.</p>
                  <p className="mt-2 text-[#F4F1EB]/80 text-sm">
                    Look out for our next family story.
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="w-full space-y-4" data-testid="newsletter-form">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name (optional)"
                    data-testid="newsletter-name"
                    className="w-full px-5 py-4 rounded-2xl border border-[#E2DCD0] bg-white focus:border-[#692128] focus:outline-none text-[#2C2A29]"
                  />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="newsletter-email"
                    className="w-full px-5 py-4 rounded-2xl border border-[#E2DCD0] bg-white focus:border-[#692128] focus:outline-none text-[#2C2A29]"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="newsletter-submit"
                    className="group w-full inline-flex items-center justify-center gap-2 bg-[#692128] text-[#F4F1EB] hover:bg-[#8B2635] disabled:opacity-70 rounded-full px-7 py-4 text-sm font-semibold tracking-wide transition-all"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Subscribing…
                      </>
                    ) : (
                      <>
                        Join our community
                        <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                  <p className="text-xs text-[#5C5856] text-center">
                    We'll never share your email. Unsubscribe anytime.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
